import React, { useEffect, useState } from 'react';
// import { useFirestoreConnect } from "react-redux-firebase";
import { Layout, Skeleton } from 'antd';
import SideNav from '../../../Layout/SideNav';
// import { getGsmPlantData, getSensor } from "../../../modules/sensors/queries";
import HeaderBar from '../../../Layout/HeaderBar';
import { useParams } from 'react-router';
import LevelChart from './Graph';
import DrumVisualization from './DrumVisualization';
import IBCVisualization from './IBCVisualization';
import WellVisualization from './WellVisualization';
import firebase from '../../../firebase';
import {
  useLatestLevelPercentage,
  type TContainerType,
} from 'Views/LevelSensorOverview/hooks/useLatestSensorLog';
import { SensorType } from '@/general/enums';
import { useTranslation } from 'react-i18next';
import LevelConfig from './LevelConfig';
import GenericAlarmList from './GenericAlarmList';
import DosagePercentage from './DosagePercentage';
import { useCustomSchema } from './hooks/useCustomSchema';
import { usePlantModuleSensorData } from './hooks/usePlantSensorData';
import { PlantModuleSchemaTable } from './PlantModuleSchemaTable';
import { PumpUsageChart } from './PumpUsageChart';
import { PumpTimeline } from './PumpTimeline';
import { DeviceActivityTimeline } from './DeviceActivityTimeline';
import { InternalSchemaEditor } from '../components/InternalSchemaTools';
import EditSensorSettings from '../components/EditSensorSettings';
import SensorInternalOverview from '../components/SensorInternalOverview';
import { subDays } from 'date-fns';
import { DateRange } from 'react-day-picker';

interface PlantModule {
  deviceId: string;
  title: string;
  containerType: TContainerType;
  thresholds?: {
    binderMin?: number;
  };
  internalResponsibles?: string[];
  dosagePercentage?: number;
  orgId: string;
  createTime: Date;
  type: SensorType.PLANT_MODULE;
}

export default function PlantModuleContainer() {
  const { orgId, sensorId } = useParams<{ orgId: string; sensorId: string }>();
  const [currentSensor, setCurrentSensor] = useState<PlantModule | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const { t } = useTranslation();

  // Load custom schema from Firestore
  const {
    customSchema,
    metadata,
    isLoading: loadingCustomSchema,
    saveCustomSchema,
  } = useCustomSchema(orgId, sensorId);

  const {
    levelPercent,
    thresholdPercent,
    levelLiters,
    thresholdLiters,
    maxCapacityLiters,
    isLoading: sensorLoading,
  } = useLatestLevelPercentage(
    currentSensor?.deviceId!,
    currentSensor?.containerType!,
    currentSensor?.thresholds?.binderMin
  );

  // Date range for data fetching - use state so it can be updated by DateRangePicker
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: subDays(new Date(), 7),
    to: new Date(),
  });

  // Get raw logs for schema table (reuses same API call)
  const {
    data: sensorData,
    isLoading: loadingLogs,
    error,
  } = usePlantModuleSensorData(currentSensor?.deviceId!, dateRange);

  console.log('[PlantModuleContainer] sensorData:', sensorData);

  useEffect(() => {
    if (!orgId || !sensorId) {
      return;
    }
    let unsubscribe: any = null;
    unsubscribe = firebase
      .firestore()
      .collection(`orgs/${orgId}/sensors`)
      .doc(sensorId)
      .onSnapshot(
        (query) => {
          if (query.exists) {
            setCurrentSensor(query.data() as PlantModule);
          }
          setIsLoading(false);
        },
        (error) => {
          console.error(error);
          setIsLoading(false);
        }
      );
    return unsubscribe;
  }, [orgId, sensorId]);

  return (
    <>
      <SideNav />
      <Layout>
        <HeaderBar />
        <div className="p-8 space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-2xl font-bold">{currentSensor?.title}</h3>
          </div>
          <SensorInternalOverview
            orgId={orgId}
            sensorId={sensorId}
            sensorType={SensorType.PLANT_MODULE}
          />
          {!isLoading && currentSensor ? (
            <div className="space-y-8">
              {/* Container Visualizations */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {/* Drum Visualization */}
                {currentSensor?.containerType === 'DRUM_220' && (
                  <div className="flex flex-col items-center">
                    <h4 className="text-lg font-semibold mb-4 text-gray-700">
                      {t('sensor.container.type.drum_220.title')}
                    </h4>
                    <DrumVisualization
                      levelPercent={levelPercent}
                      thresholdPercent={thresholdPercent}
                      currentLiters={levelLiters}
                      thresholdLiters={thresholdLiters}
                      maxCapacityLiters={maxCapacityLiters}
                    />
                  </div>
                )}

                {/* IBC Visualization */}
                {currentSensor?.containerType === 'IBC_1000' && (
                  <div className="flex flex-col items-center">
                    <h4 className="text-lg font-semibold mb-4 text-gray-700">
                      {t('sensor.container.type.ibc_1000.title')}
                    </h4>
                    <IBCVisualization
                      levelPercent={levelPercent}
                      thresholdPercent={thresholdPercent}
                      currentLiters={levelLiters}
                      thresholdLiters={thresholdLiters}
                      maxCapacityLiters={maxCapacityLiters}
                    />
                  </div>
                )}

                {/* Well Visualization */}
                {currentSensor?.containerType === 'WELL_10' && (
                  <div className="flex flex-col items-center">
                    <h4 className="text-lg font-semibold mb-4 text-gray-700">
                      {t('sensor.container.type.well_10.title')}
                    </h4>
                    <WellVisualization
                      levelPercent={levelPercent}
                      thresholdPercent={thresholdPercent}
                      currentLiters={levelLiters}
                      thresholdLiters={thresholdLiters}
                      maxCapacityLiters={maxCapacityLiters}
                      wellDepth={10}
                    />
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <LevelConfig deviceId={currentSensor?.deviceId} />
                <GenericAlarmList deviceId={currentSensor?.deviceId} />
              </div>

              <EditSensorSettings
                orgId={orgId!}
                sensorId={sensorId!}
                sensorType={currentSensor.type}
                currentInternalResponsibles={currentSensor.internalResponsibles}
                currentContainerType={currentSensor.containerType}
              />

              <DosagePercentage
                orgId={orgId}
                sensorId={sensorId}
                initialValue={currentSensor?.dosagePercentage}
              />

              {/* Level Chart */}
              <div className="w-full">
                <h4 className="text-lg font-semibold mb-4 text-gray-700">
                  {t('sensor.container.type.level_history.title')}
                </h4>
                <LevelChart
                  id={currentSensor?.deviceId}
                  containerType={currentSensor?.containerType}
                  thresholds={currentSensor.thresholds}
                  data={sensorData}
                  isLoading={loadingLogs}
                  error={error}
                  dateRange={dateRange}
                  onDateChange={setDateRange}
                />
              </div>

              {/* Pump Activity Timeline */}
              {!loadingLogs && sensorData?.pumpRunPeriods && (
                <PumpTimeline pumpRunPeriods={sensorData.pumpRunPeriods} />
              )}

              {/* Device Activity Timelines */}
              {!loadingLogs &&
                sensorData?.deviceActivity &&
                sensorData.deviceActivity.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <DeviceActivityTimeline deviceActivity={sensorData.deviceActivity} field="v1" />
                    <DeviceActivityTimeline deviceActivity={sensorData.deviceActivity} field="v2" />
                    <DeviceActivityTimeline deviceActivity={sensorData.deviceActivity} field="ex" />
                    <DeviceActivityTimeline deviceActivity={sensorData.deviceActivity} field="on" />
                  </div>
                )}

              {/* Schema Table */}
              <div className="w-full">
                <div className="flex justify-between items-center">
                  <h4 className="text-lg font-semibold mb-4 text-gray-700">
                    Log skema (seneste 7 dage)
                  </h4>
                  <InternalSchemaEditor
                    variant="plant"
                    customSchema={customSchema}
                    metadata={metadata}
                    onSave={saveCustomSchema}
                  />
                </div>
                {loadingLogs ? (
                  <Skeleton />
                ) : (
                  <PlantModuleSchemaTable
                    logs={sensorData?.rawLogs || []}
                    customSchema={customSchema}
                    metadata={metadata}
                  />
                )}
              </div>

              {/* Pump Usage Chart */}
              {/* {!loadingLogs && sensorData?.pumpStats && (
                <PumpUsageChart pumpStats={sensorData.pumpStats} />
              )} */}
            </div>
          ) : (
            <Skeleton />
          )}
        </div>
      </Layout>
    </>
  );
}
